import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  fetchMediaItems,
  fetchMediaSummary,
  fetchMediaDuplicates,
  fetchMediaUploaders,
  trashMediaItem as apiTrash,
  restoreMediaItem as apiRestore,
  bulkTrashMedia as apiBulkTrash,
  bulkRestoreMedia as apiBulkRestore,
  bulkPurgeMedia as apiBulkPurge,
  emptyMediaTrash as apiEmptyTrash,
  recalculateMediaUsage as apiRecalculate,
} from '../../api/workspaceMediaApi';

const defaultFilters = {
  source: '',
  uploader: '',
  q: '',
  sort: '-uploadedAt',
  trash: '0',
  uploadedFrom: '',
  uploadedTo: '',
};

const emptyBucket = () => ({
  items: [],
  pagination: { page: 1, limit: 50, total: 0, pages: 0 },
  status: 'idle',
  loadedOnce: false,
});

const initialState = {
  workspaceId: null,
  summary: null,
  filters: { ...defaultFilters },
  // Two independent buckets keyed by tab — switching tabs shows the cached
  // data instantly while the next load refreshes it in the background.
  all: emptyBucket(),
  trash: emptyBucket(),
  duplicates: {
    groups: [],
    totalGroups: 0,
    totalWasted: 0,
    status: 'idle',
    loadedOnce: false,
    filters: { source: '', q: '', sort: '-wastedBytes' },
  },
  uploaders: { list: [], status: 'idle' },
  summaryStatus: 'idle',
  // In-flight item ids (per-row spinner) + a bulk-action flag (bar spinner).
  pendingIds: [],
  bulkActionInFlight: false,
  error: null,
};

const RETENTION_MS = 30 * 24 * 60 * 60 * 1000; // mirror server default

const removeFromBucket = (bucket, ids) => {
  const idSet = new Set(ids);
  const removed = bucket.items.filter((i) => idSet.has(i._id));
  bucket.items = bucket.items.filter((i) => !idSet.has(i._id));
  bucket.pagination.total = Math.max(0, bucket.pagination.total - removed.length);
  return removed;
};

const bucketKey = (filters) => (filters?.trash === '1' ? 'trash' : 'all');

const pruneDuplicates = (state, ids) => {
  if (!state.duplicates?.groups?.length || !ids?.length) return;
  const idSet = new Set(ids.map(String));
  const next = [];
  let wastedDelta = 0;
  for (const group of state.duplicates.groups) {
    const items = group.items.filter((it) => !idSet.has(String(it._id)));
    const removed = group.items.length - items.length;
    if (!removed) {
      next.push(group);
      continue;
    }
    const newCount = group.count - removed;
    if (newCount > 1) {
      const newWasted = group.size * (newCount - 1);
      wastedDelta += newWasted - group.wastedBytes;
      next.push({ ...group, items, count: newCount, wastedBytes: newWasted });
    } else {
      wastedDelta -= group.wastedBytes;
    }
  }
  state.duplicates.groups = next;
  state.duplicates.totalGroups = next.length;
  state.duplicates.totalWasted = Math.max(0, state.duplicates.totalWasted + wastedDelta);
};

export const loadMedia = createAsyncThunk(
  'workspaceMedia/load',
  async ({ workspaceId, filters = {}, page = 1, limit = 50 }, { rejectWithValue }) => {
    try {
      const data = await fetchMediaItems(workspaceId, { ...filters, page, limit });
      return { workspaceId, bucket: bucketKey(filters), ...data };
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to load media');
    }
  }
);

export const loadUploaders = createAsyncThunk(
  'workspaceMedia/loadUploaders',
  async ({ workspaceId }, { rejectWithValue }) => {
    try {
      const data = await fetchMediaUploaders(workspaceId);
      return data?.uploaders || [];
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to load uploaders');
    }
  }
);

export const loadDuplicates = createAsyncThunk(
  'workspaceMedia/loadDuplicates',
  async ({ workspaceId, filters = {} }, { rejectWithValue }) => {
    try {
      const data = await fetchMediaDuplicates(workspaceId, filters);
      return data;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to load duplicates');
    }
  }
);

export const loadMediaSummary = createAsyncThunk(
  'workspaceMedia/loadSummary',
  async ({ workspaceId }, { rejectWithValue }) => {
    try {
      const data = await fetchMediaSummary(workspaceId);
      return { workspaceId, summary: data };
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to load summary');
    }
  }
);

export const trashItem = createAsyncThunk(
  'workspaceMedia/trash',
  async ({ workspaceId, itemId, permanent }, { rejectWithValue }) => {
    try {
      await apiTrash(workspaceId, itemId, { permanent });
      return { itemId, permanent: !!permanent };
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to delete');
    }
  }
);

export const restoreItem = createAsyncThunk(
  'workspaceMedia/restore',
  async ({ workspaceId, itemId }, { rejectWithValue }) => {
    try {
      const data = await apiRestore(workspaceId, itemId);
      return { itemId, item: data?.item };
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to restore');
    }
  }
);

export const bulkTrash = createAsyncThunk(
  'workspaceMedia/bulkTrash',
  async ({ workspaceId, itemIds }, { rejectWithValue }) => {
    try {
      await apiBulkTrash(workspaceId, itemIds);
      return { itemIds };
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to bulk delete');
    }
  }
);

export const bulkRestore = createAsyncThunk(
  'workspaceMedia/bulkRestore',
  async ({ workspaceId, itemIds }, { rejectWithValue }) => {
    try {
      const data = await apiBulkRestore(workspaceId, itemIds);
      return { restored: data?.restored || [], failed: data?.failed || [] };
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to bulk restore');
    }
  }
);

export const bulkPurge = createAsyncThunk(
  'workspaceMedia/bulkPurge',
  async ({ workspaceId, itemIds }, { rejectWithValue }) => {
    try {
      const data = await apiBulkPurge(workspaceId, itemIds);
      return { purged: data?.purged || [], failed: data?.failed || [] };
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to bulk delete');
    }
  }
);

export const recalculateUsage = createAsyncThunk(
  'workspaceMedia/recalculate',
  async ({ workspaceId }, { rejectWithValue }) => {
    try {
      return await apiRecalculate(workspaceId);
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to recalculate');
    }
  }
);

export const emptyTrash = createAsyncThunk(
  'workspaceMedia/emptyTrash',
  async ({ workspaceId }, { rejectWithValue }) => {
    try {
      await apiEmptyTrash(workspaceId);
      return {};
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to empty trash');
    }
  }
);

const slice = createSlice({
  name: 'workspaceMedia',
  initialState,
  reducers: {
    setFilters(state, action) {
      state.filters = { ...state.filters, ...action.payload };
    },
    resetFilters(state) {
      state.filters = { ...defaultFilters };
    },
    setDuplicateFilters(state, action) {
      state.duplicates.filters = { ...state.duplicates.filters, ...action.payload };
    },
    /* Socket-driven mutations */
    mediaItemAdded(state, action) {
      const { item, newUsage } = action.payload;
      if (state.summary && typeof newUsage === 'number') {
        state.summary.usage = newUsage;
        if (state.summary.limit && state.summary.limit !== null) {
          state.summary.usagePercentage = Math.min(100, (newUsage / state.summary.limit) * 100);
        }
      }
      if (item && !state.all.items.some((i) => i._id === item._id)) {
        state.all.items.unshift(item);
        state.all.pagination.total += 1;
      }
    },
    mediaItemTrashed(state, action) {
      const { itemId, newUsage, purgeAt } = action.payload;
      if (state.summary && typeof newUsage === 'number') state.summary.usage = newUsage;
      pruneDuplicates(state, [itemId]);

      const moving = state.all.items.find((i) => i._id === itemId);
      state.all.items = state.all.items.filter((i) => i._id !== itemId);
      state.all.pagination.total = Math.max(0, state.all.pagination.total - 1);
      if (moving) {
        const trashed = { ...moving, deletedAt: new Date().toISOString(), purgeAt };
        state.trash.items.unshift(trashed);
        state.trash.pagination.total += 1;
        if (state.summary) state.summary.trashedSize = (state.summary.trashedSize || 0) + (moving.size || 0);
      }
    },
    mediaItemRestored(state, action) {
      const { item, newUsage } = action.payload;
      if (state.summary && typeof newUsage === 'number') state.summary.usage = newUsage;
      if (!item?._id) return;
      state.trash.items = state.trash.items.filter((i) => i._id !== item._id);
      state.trash.pagination.total = Math.max(0, state.trash.pagination.total - 1);
      if (!state.all.items.some((i) => i._id === item._id)) {
        state.all.items.unshift(item);
        state.all.pagination.total += 1;
      }
      if (state.summary) state.summary.trashedSize = Math.max(0, (state.summary.trashedSize || 0) - (item.size || 0));
    },
    mediaItemPurged(state, action) {
      const { itemId } = action.payload;
      state.trash.items = state.trash.items.filter((i) => i._id !== itemId);
      state.trash.pagination.total = Math.max(0, state.trash.pagination.total - 1);
    },
    storageLimitChanged(state, action) {
      if (state.summary) {
        state.summary.limit = action.payload.limit;
        state.summary.isUnlimited = action.payload.limit === null;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadMedia.pending, (state, action) => {
        const key = bucketKey(action.meta.arg?.filters);
        state[key].status = 'loading';
        state.error = null;
      })
      .addCase(loadMedia.fulfilled, (state, action) => {
        const key = action.payload.bucket;
        state.workspaceId = action.payload.workspaceId;
        state[key].items = action.payload.items || [];
        state[key].pagination = action.payload.pagination || emptyBucket().pagination;
        state[key].status = 'succeeded';
        state[key].loadedOnce = true;
      })
      .addCase(loadMedia.rejected, (state, action) => {
        const key = bucketKey(action.meta.arg?.filters);
        state[key].status = 'failed';
        state.error = action.payload || action.error?.message;
      })
      .addCase(loadUploaders.pending, (state) => { state.uploaders.status = 'loading'; })
      .addCase(loadUploaders.fulfilled, (state, action) => {
        state.uploaders.status = 'succeeded';
        state.uploaders.list = action.payload || [];
      })
      .addCase(loadUploaders.rejected, (state) => { state.uploaders.status = 'failed'; })
      .addCase(loadDuplicates.pending, (state) => {
        state.duplicates.status = 'loading';
      })
      .addCase(loadDuplicates.fulfilled, (state, action) => {
        state.duplicates.status = 'succeeded';
        state.duplicates.loadedOnce = true;
        state.duplicates.groups = action.payload.groups || [];
        state.duplicates.totalGroups = action.payload.totalGroups || 0;
        state.duplicates.totalWasted = action.payload.totalWasted || 0;
      })
      .addCase(loadDuplicates.rejected, (state, action) => {
        state.duplicates.status = 'failed';
        state.error = action.payload || action.error?.message;
      })
      .addCase(loadMediaSummary.pending, (state) => { state.summaryStatus = 'loading'; })
      .addCase(loadMediaSummary.fulfilled, (state, action) => {
        state.summaryStatus = 'succeeded';
        state.summary = action.payload.summary;
      })
      .addCase(loadMediaSummary.rejected, (state, action) => {
        state.summaryStatus = 'failed';
        state.error = action.payload || action.error?.message;
      })
      /* trashItem ----------------------------------------------------- */
      .addCase(trashItem.pending, (state, action) => {
        const id = action.meta.arg?.itemId;
        if (id && !state.pendingIds.includes(id)) state.pendingIds.push(id);
      })
      .addCase(trashItem.fulfilled, (state, action) => {
        const { itemId, permanent } = action.payload;
        state.pendingIds = state.pendingIds.filter((i) => i !== itemId);
        pruneDuplicates(state, [itemId]);

        if (permanent) {
          removeFromBucket(state.all, [itemId]);
          removeFromBucket(state.trash, [itemId]);
          return;
        }
        // Non-permanent: move all → trash optimistically.
        const [moved] = removeFromBucket(state.all, [itemId]);
        if (moved) {
          const now = new Date();
          const trashedCopy = {
            ...moved,
            deletedAt: now.toISOString(),
            purgeAt: new Date(now.getTime() + RETENTION_MS).toISOString(),
          };
          state.trash.items.unshift(trashedCopy);
          state.trash.pagination.total += 1;
          if (state.summary) {
            state.summary.usage = Math.max(0, (state.summary.usage || 0) - (moved.size || 0));
            state.summary.trashedSize = (state.summary.trashedSize || 0) + (moved.size || 0);
            state.summary.trashedCount = (state.summary.trashedCount || 0) + 1;
            if (state.summary.limit) {
              state.summary.usagePercentage = Math.min(100, (state.summary.usage / state.summary.limit) * 100);
            }
          }
        }
      })
      .addCase(trashItem.rejected, (state, action) => {
        const id = action.meta.arg?.itemId;
        state.pendingIds = state.pendingIds.filter((i) => i !== id);
      })

      /* restoreItem --------------------------------------------------- */
      .addCase(restoreItem.pending, (state, action) => {
        const id = action.meta.arg?.itemId;
        if (id && !state.pendingIds.includes(id)) state.pendingIds.push(id);
      })
      .addCase(restoreItem.fulfilled, (state, action) => {
        const { itemId } = action.payload;
        state.pendingIds = state.pendingIds.filter((i) => i !== itemId);

        const [moved] = removeFromBucket(state.trash, [itemId]);
        if (moved) {
          const restored = { ...moved, deletedAt: null, purgeAt: null };
          state.all.items.unshift(restored);
          state.all.pagination.total += 1;
          if (state.summary) {
            state.summary.usage = (state.summary.usage || 0) + (moved.size || 0);
            state.summary.trashedSize = Math.max(0, (state.summary.trashedSize || 0) - (moved.size || 0));
            state.summary.trashedCount = Math.max(0, (state.summary.trashedCount || 0) - 1);
            if (state.summary.limit) {
              state.summary.usagePercentage = Math.min(100, (state.summary.usage / state.summary.limit) * 100);
            }
          }
        }
      })
      .addCase(restoreItem.rejected, (state, action) => {
        const id = action.meta.arg?.itemId;
        state.pendingIds = state.pendingIds.filter((i) => i !== id);
      })

      /* bulkTrash ----------------------------------------------------- */
      .addCase(bulkTrash.pending, (state) => {
        state.bulkActionInFlight = true;
      })
      .addCase(bulkTrash.fulfilled, (state, action) => {
        state.bulkActionInFlight = false;
        const ids = action.payload.itemIds || [];
        pruneDuplicates(state, ids);
        const removed = removeFromBucket(state.all, ids);
        const now = new Date();
        const purgeAt = new Date(now.getTime() + RETENTION_MS).toISOString();
        let totalSize = 0;
        for (const m of removed) {
          totalSize += m.size || 0;
          state.trash.items.unshift({ ...m, deletedAt: now.toISOString(), purgeAt });
        }
        state.trash.pagination.total += removed.length;
        if (state.summary && totalSize > 0) {
          state.summary.usage = Math.max(0, (state.summary.usage || 0) - totalSize);
          state.summary.trashedSize = (state.summary.trashedSize || 0) + totalSize;
          state.summary.trashedCount = (state.summary.trashedCount || 0) + removed.length;
          if (state.summary.limit) {
            state.summary.usagePercentage = Math.min(100, (state.summary.usage / state.summary.limit) * 100);
          }
        }
      })
      .addCase(bulkTrash.rejected, (state) => {
        state.bulkActionInFlight = false;
      })

      /* bulkRestore --------------------------------------------------- */
      .addCase(bulkRestore.pending, (state) => {
        state.bulkActionInFlight = true;
      })
      .addCase(bulkRestore.fulfilled, (state, action) => {
        state.bulkActionInFlight = false;
        const restored = action.payload.restored || [];
        const ids = restored.map((r) => r._id);
        const removed = removeFromBucket(state.trash, ids);
        let totalSize = 0;
        for (const m of removed) totalSize += m.size || 0;
        for (const item of restored) {
          if (!state.all.items.some((i) => i._id === item._id)) {
            state.all.items.unshift({ ...item, deletedAt: null, purgeAt: null });
            state.all.pagination.total += 1;
          }
        }
        if (state.summary && totalSize > 0) {
          state.summary.usage = (state.summary.usage || 0) + totalSize;
          state.summary.trashedSize = Math.max(0, (state.summary.trashedSize || 0) - totalSize);
          state.summary.trashedCount = Math.max(0, (state.summary.trashedCount || 0) - removed.length);
          if (state.summary.limit) {
            state.summary.usagePercentage = Math.min(100, (state.summary.usage / state.summary.limit) * 100);
          }
        }
      })
      .addCase(bulkRestore.rejected, (state) => {
        state.bulkActionInFlight = false;
      })

      /* bulkPurge ----------------------------------------------------- */
      .addCase(bulkPurge.pending, (state) => {
        state.bulkActionInFlight = true;
      })
      .addCase(bulkPurge.fulfilled, (state, action) => {
        state.bulkActionInFlight = false;
        const ids = action.payload.purged || [];
        removeFromBucket(state.trash, ids);
        if (state.summary) {
          state.summary.trashedCount = Math.max(0, (state.summary.trashedCount || 0) - ids.length);
        }
      })
      .addCase(bulkPurge.rejected, (state) => {
        state.bulkActionInFlight = false;
      })

      /* emptyTrash ---------------------------------------------------- */
      .addCase(emptyTrash.pending, (state) => {
        state.bulkActionInFlight = true;
      })
      .addCase(emptyTrash.fulfilled, (state) => {
        state.bulkActionInFlight = false;
        state.trash.items = [];
        state.trash.pagination.total = 0;
        if (state.summary) {
          state.summary.trashedSize = 0;
          state.summary.trashedCount = 0;
        }
      })
      .addCase(emptyTrash.rejected, (state) => {
        state.bulkActionInFlight = false;
      });
  },
});

export const {
  setFilters,
  resetFilters,
  setDuplicateFilters,
  mediaItemAdded,
  mediaItemTrashed,
  mediaItemRestored,
  mediaItemPurged,
  storageLimitChanged,
} = slice.actions;

/* Selectors */
export const selectMediaBucket = (state) => state.workspaceMedia[bucketKey(state.workspaceMedia.filters)];
export const selectMediaItems = (state) => selectMediaBucket(state).items;
export const selectMediaPagination = (state) => selectMediaBucket(state).pagination;
export const selectMediaStatus = (state) => selectMediaBucket(state).status;
export const selectMediaLoadedOnce = (state) => selectMediaBucket(state).loadedOnce;
export const selectMediaPendingIds = (state) => state.workspaceMedia.pendingIds;
export const selectMediaBulkInFlight = (state) => state.workspaceMedia.bulkActionInFlight;

export default slice.reducer;
